import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { FaTimes, FaTruck, FaCheckCircle, FaBoxOpen } from 'react-icons/fa';
import toast from 'react-hot-toast';
import './RecibirCompraModal.css';
import { RECIBIR_COMPRA, GET_COMPRAS } from '../../graphql/compras';

// Usage:
// <RecibirCompraModal isOpen={open} onClose={() => setOpen(false)} compra={compraObj} />
// compra must include: { id, codigo, proveedor, detalles: [{ id, nombre_item, cantidad, cantidad_recibida, ... }] }

const RecibirCompraModal = ({ isOpen, onClose, compra }) => {
  const [cantidades, setCantidades] = useState({});

  const [recibirCompra, { loading }] = useMutation(RECIBIR_COMPRA, {
    refetchQueries: [{ query: GET_COMPRAS }]
  });

  // Initialize input quantities to pendiente (ordered - already received)
  useEffect(() => {
    if (compra?.detalles) {
      const initial = {};
      compra.detalles.forEach((d) => {
        const pendiente = (parseFloat(d.cantidad) || 0) - (parseFloat(d.cantidad_recibida) || 0);
        initial[d.id] = Math.max(0, pendiente).toString();
      });
      setCantidades(initial);
    }
  }, [compra, isOpen]);

  const handleCantidadChange = (detalleId, value) => {
    setCantidades((prev) => ({ ...prev, [detalleId]: value }));
  };

  const handleMaximizar = (detalle) => {
    const pendiente = (parseFloat(detalle.cantidad) || 0) - (parseFloat(detalle.cantidad_recibida) || 0);
    setCantidades((prev) => ({ ...prev, [detalle.id]: Math.max(0, pendiente).toString() }));
  };

  const handleLimpiar = (detalleId) => {
    setCantidades((prev) => ({ ...prev, [detalleId]: '0' }));
  };

  const totalARecibir = Object.values(cantidades).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const detallesInput = Object.entries(cantidades)
      .filter(([, v]) => parseFloat(v) > 0)
      .map(([detalleId, cantidad]) => ({
        detalle_compra_id: parseInt(detalleId),
        cantidad_recibida: parseFloat(cantidad)
      }));

    if (detallesInput.length === 0) {
      toast.error('Ingrese al menos una cantidad a recibir mayor a cero');
      return;
    }

    // Validate no quantity exceeds pending
    const errores = [];
    detallesInput.forEach(({ detalle_compra_id, cantidad_recibida }) => {
      const detalle = compra.detalles.find((d) => d.id.toString() === detalle_compra_id.toString());
      if (detalle) {
        const pendiente =
          (parseFloat(detalle.cantidad) || 0) - (parseFloat(detalle.cantidad_recibida) || 0);
        if (cantidad_recibida > pendiente) {
          errores.push(
            `"${detalle.nombre_item}": cantidad a recibir (${cantidad_recibida}) supera la pendiente (${pendiente})`
          );
        }
      }
    });

    if (errores.length > 0) {
      toast.error(errores[0]);
      return;
    }

    try {
      await recibirCompra({
        variables: {
          input: {
            compra_id: parseInt(compra.id),
            detalles: detallesInput
          }
        }
      });
      toast.success('Recepcion de mercancia registrada correctamente');
      onClose();
    } catch (error) {
      console.error('Error al recibir compra:', error);
      toast.error(error.message || 'Error al registrar la recepcion');
    }
  };

  if (!isOpen || !compra) return null;

  const detalles = compra.detalles || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="recibir-compra-modal" onClick={(e) => e.stopPropagation()}>
        <div className="recibir-compra-header">
          <div className="header-info">
            <h2>
              <FaTruck />
              Recibir Mercancia
            </h2>
            <span className="header-codigo">
              {compra.codigo} — {compra.proveedor?.nombre_comercial || compra.proveedor?.nombre}
            </span>
          </div>
          <button className="close-button" onClick={onClose} type="button" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>

        <form className="recibir-compra-form" onSubmit={handleSubmit}>
          <div className="recibir-info-banner">
            <FaBoxOpen />
            <p>
              Indique las cantidades efectivamente recibidas para cada articulo. Solo se actualiza
              el inventario con las cantidades que ingrese.
            </p>
          </div>

          <div className="recibir-table-wrapper">
            <table className="recibir-table">
              <thead>
                <tr>
                  <th>Articulo</th>
                  <th className="text-center">Ordenado</th>
                  <th className="text-center">Ya Recibido</th>
                  <th className="text-center">Pendiente</th>
                  <th className="text-center">A Recibir Ahora</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {detalles.map((detalle) => {
                  const ordenado = parseFloat(detalle.cantidad) || 0;
                  const yaRecibido = parseFloat(detalle.cantidad_recibida) || 0;
                  const pendiente = Math.max(0, ordenado - yaRecibido);
                  const aRecibir = parseFloat(cantidades[detalle.id] || '0') || 0;
                  const completo = yaRecibido >= ordenado;

                  return (
                    <tr
                      key={detalle.id}
                      className={`recibir-row ${completo ? 'recibir-row--completo' : ''}`}
                    >
                      <td className="item-nombre">
                        {completo && (
                          <FaCheckCircle className="completo-icon" title="Totalmente recibido" />
                        )}
                        <span>{detalle.nombre_item}</span>
                        {detalle.item?.codigo && (
                          <span className="item-codigo">[{detalle.item.codigo}]</span>
                        )}
                      </td>
                      <td className="text-center cantidad-badge">{ordenado}</td>
                      <td className="text-center">
                        <span className={`recibido-badge ${yaRecibido > 0 ? 'recibido-badge--some' : ''}`}>
                          {yaRecibido}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={`pendiente-badge ${pendiente > 0 ? 'pendiente-badge--active' : 'pendiente-badge--zero'}`}>
                          {pendiente}
                        </span>
                      </td>
                      <td className="text-center">
                        <input
                          type="number"
                          value={cantidades[detalle.id] || '0'}
                          onChange={(e) => handleCantidadChange(detalle.id, e.target.value)}
                          min="0"
                          max={pendiente}
                          step="0.001"
                          className="cantidad-input"
                          disabled={completo}
                        />
                      </td>
                      <td>
                        {!completo && (
                          <div className="recibir-actions-cell">
                            <button
                              type="button"
                              className="btn-max"
                              onClick={() => handleMaximizar(detalle)}
                              title="Recibir todo lo pendiente"
                            >
                              Max
                            </button>
                            <button
                              type="button"
                              className="btn-cero"
                              onClick={() => handleLimpiar(detalle.id)}
                              title="Poner en cero"
                            >
                              0
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="recibir-footer-info">
            <span>Total unidades a registrar: <strong>{totalARecibir.toFixed(3)}</strong></span>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-guardar" disabled={loading || totalARecibir === 0}>
              <FaTruck />
              {loading ? 'Registrando...' : 'Confirmar Recepcion'}
            </button>
            <button type="button" className="btn-cancelar" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecibirCompraModal;
